/**
 * Home Page — CHI Alliance POC Mockup
 * Obsidian Pulse design system — natural-case DM Serif Display hero typography and a graphic rotating statement keep the community story confident and readable.
 */
import { Link } from "wouter";
import gsap from "gsap";
import Navigation from "@/components/Navigation";
import SectionBadge from "@/components/SectionBadge";
import { FloatingOrb } from "@/components/FloatingOrb";
import Footer from "@/components/Footer";
import MembershipBanner from "@/components/MembershipBanner";
import PremierPartners from "@/components/PremierPartners";
import HomepagePromotion from "@/components/HomepagePromotion";
import CtaIcon from "@/components/CtaIcon";
import EditorialAccordionShowcase from "@/components/EditorialAccordionShowcase";
import HomeFundamentalsExperience from "@/components/HomeFundamentalsExperience";
import HomeMembershipCommunityHero from "@/components/HomeMembershipCommunityHero";
import { ArrowRight, CalendarDays, Handshake, Heart, Lightbulb, Users, TrendingUp, Play, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { FadeUp, FlowIn, ScaleReveal } from "@/components/ScrollAnimations";
import ResponsiveImage from "@/components/ResponsiveImage";
import { responsiveImageSrcSet } from "@shared/responsiveImages";


const faqs = [
  {
    q: "What is The CHI Alliance?",
    a: "The CHI Alliance (Connecting Human Innovators) is a professional community that brings together HR leaders, entrepreneurs, founders, business professionals, consultants, educators, and innovators who believe in the power of human connection. Our mission is to create meaningful relationships, share knowledge, and help individuals and organizations grow through collaboration, learning, and community.",
  },
  {
    q: "Why was The CHI Alliance created?",
    a: "We believe that growth happens faster when people learn, connect, and innovate together. The CHI Alliance was created to provide a trusted space where professionals can exchange ideas, solve challenges, gain new perspectives, and build relationships that create lasting personal and professional impact.",
  },
  {
    q: "What can members expect from The CHI Alliance?",
    a: "Members gain access to monthly virtual roundtables, networking events, guest speakers, panel discussions, peer learning opportunities, leadership conversations, and collaborative problem-solving sessions. Every experience is designed to provide practical insights, meaningful connections, and actionable ideas.",
  },
  {
    q: "Who should join The CHI Alliance?",
    a: "The CHI Alliance is for professionals passionate about people, leadership, innovation, learning, and community. Whether you work in HR, Talent, L&D, Benefits, Operations, Legal, Health & Wellness, Entrepreneurship, or Business Leadership, you will find opportunities to connect with like-minded professionals.",
  },
  {
    q: "What makes The CHI Alliance different?",
    a: "Unlike traditional networking groups, The CHI Alliance focuses on building authentic relationships and creating value through shared wisdom and collaboration. Our goal is not simply to exchange business cards but to foster a community where members support one another, learn together, and create opportunities that elevate people, organizations, and communities.",
  },
  {
    q: "How often does The CHI Alliance meet?",
    a: "Our vision is to host monthly virtual roundtables, networking events, leadership discussions, and special community gatherings. Additional workshops, happy hours, and in-person events may be offered throughout the year.",
  },
];

/**
 * The rotating community statements, each paired with the photograph it plays over.
 *
 * Photographs chosen to make the community tangible: an after-hours gathering,
 * a focused micro-learning session, a small salon conversation, a member sharing
 * expertise, and a curated dinner. The card should feel like an invitation into
 * the calendar—not another pass at the site's generic group photography.
 *
 * They load as the rotation reaches them rather than all at once: five full-size
 * backgrounds requested up front would cost around a megabyte for a card that sits
 * far below the fold.
 */
const ROTATING_STATEMENTS = [
  {
    text: "Happy hour conversations turn first introductions into relationships that keep moving.",
    image: "/media/events/membership-happy-hour.webp",
    position: "center 34%",
  },
  {
    text: "Micro-learning on AI gives members a practical idea they can use before the week is over.",
    image: "/media/events/featured-session-stage.webp",
    position: "center center",
  },
  {
    text: "Small-room salons create the kind of candid exchange that changes how you see a challenge.",
    image: "/media/events/salon-networking.webp",
    position: "34% center",
  },
  {
    text: "Share expertise, practice the idea out loud, and leave with momentum.",
    image: "/media/events/share-your-expertise.webp",
    position: "68% center",
  },
  {
    text: "Curated dinners make space for big questions, thoughtful peers, and useful perspective.",
    image: "/media/events/executive-dinner.webp",
    position: "center center",
  },
] as const;

/** Both photo layers rest here, so a crossfade lands on the same exposure. */
const STATEMENT_IMAGE_OPACITY = 0.7;

/**
 * Slide two's backdrop and portrait, kept as two files rather than one plate.
 *
 * They used to be a single composite hosted off-site, which meant Harry could
 * only be resized by re-cutting the image — the gradient and the constellations
 * scaled with him. Split apart, his size is the one number below, and the
 * backdrop stays put underneath.
 */
function RotatingStatement() {
  const indexRef = useRef(0);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const textARef = useRef<HTMLParagraphElement>(null);
  const textBRef = useRef<HTMLParagraphElement>(null);
  const imageARef = useRef<HTMLImageElement>(null);
  const imageBRef = useRef<HTMLImageElement>(null);
  const activeRef = useRef<'a' | 'b'>('a');

  const textStyle: React.CSSProperties = {
    margin: 0,
    position: "absolute",
    inset: 0,
    left: 0,
    right: 0,
  };

  useEffect(() => {
    const textA = textARef.current;
    const textB = textBRef.current;
    const imageA = imageARef.current;
    const imageB = imageBRef.current;
    if (!textA || !textB || !imageA || !imageB) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // imageA already carries the opening frame from the markup.
    textA.textContent = ROTATING_STATEMENTS[0].text;
    gsap.set(textA, { opacity: 1, y: 0 });
    gsap.set(textB, { opacity: 0, y: 12 });
    gsap.set(imageA, { opacity: STATEMENT_IMAGE_OPACITY, scale: 1 });
    gsap.set(imageB, { opacity: 0, scale: 1 });

    if (prefersReducedMotion) return;

    const cycle = () => {
      const next = (indexRef.current + 1) % ROTATING_STATEMENTS.length;
      const showingA = activeRef.current === 'a';
      const outText = showingA ? textA : textB;
      const inText = showingA ? textB : textA;
      const outImage = showingA ? imageA : imageB;
      const inImage = showingA ? imageB : imageA;

      inText.textContent = ROTATING_STATEMENTS[next].text;
      /*
       * Assigned now, at the start of the dwell rather than at the start of the
       * crossfade, which gives the browser the full delay below to fetch and decode
       * it. Fading toward a layer that has not decoded yet shows a hole in the card.
       */
      if (inImage.getAttribute("src") !== ROTATING_STATEMENTS[next].image) {
        inImage.src = ROTATING_STATEMENTS[next].image;
        inImage.srcset = responsiveImageSrcSet(ROTATING_STATEMENTS[next].image) ?? "";
      }
      inImage.style.objectPosition = ROTATING_STATEMENTS[next].position;

      const tl = gsap.timeline({
        delay: 3.8,
        onComplete: () => {
          indexRef.current = next;
          activeRef.current = showingA ? 'b' : 'a';
          cycle();
        },
      });
      tlRef.current = tl;

      /*
       * A blend rather than a handoff: the two photographs overlap for about a
       * second at matched opacity, and the words cross within that same window. The
       * slow counter-scale keeps the picture from feeling like a static plate
       * swapping out underneath the type.
       */
      tl.to(outImage, { opacity: 0, scale: 1.035, duration: 1.15, ease: "sine.inOut" }, 0)
        .fromTo(inImage,
          { opacity: 0, scale: 1.045 },
          { opacity: STATEMENT_IMAGE_OPACITY, scale: 1, duration: 1.25, ease: "sine.inOut" },
          0,
        )
        // Small vertical drift only. Under a crossfade, larger movement reads as two
        // competing sentences rather than one dissolving into the next.
        .to(outText, { opacity: 0, y: -14, duration: 0.85, ease: "sine.in" }, 0.1)
        .fromTo(inText,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.95, ease: "sine.out" },
          0.42,
        );
    };

    cycle();
    return () => { tlRef.current?.kill(); };
  }, []);

  return (
    <>
      {/*
        Both layers are lazy and alt-less: they are the card's backdrop, and the
        statement over them is the content a screen reader should receive.
      */}
      <img
        ref={imageARef}
        className="home-community-card__photo"
        // The opening frame is in the markup so the card is never briefly empty and
        // the preload scanner can find it. The second layer stays sourceless until
        // the first swap needs it, which is what keeps the other four off the
        // initial page load.
        src={ROTATING_STATEMENTS[0].image}
        srcSet={responsiveImageSrcSet(ROTATING_STATEMENTS[0].image)}
        sizes="(max-width: 760px) 92vw, 50vw"
        style={{
          opacity: STATEMENT_IMAGE_OPACITY,
          objectPosition: ROTATING_STATEMENTS[0].position,
        }}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
      <img ref={imageBRef} className="home-community-card__photo" alt="" aria-hidden="true" loading="lazy" decoding="async" />
      <div className="home-community-card__scrim" aria-hidden="true" />
      <div className="home-community-card__statement">
        <div className="home-rotating-statement" aria-live="polite">
          <div className="home-rotating-statement__message">
            <p ref={textARef} style={textStyle} />
            <p ref={textBRef} style={textStyle} />
          </div>
        </div>
      </div>
    </>
  );
}
const COMMUNITY_HERO_BACKGROUND = "/media/backgrounds/community-hero-network.webp";

function HomeHeroCarousel() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(media.matches);
    updateMotionPreference();
    media.addEventListener("change", updateMotionPreference);
    return () => media.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const timer = window.setInterval(() => setActiveSlide((current) => (current + 1) % 2), 8500);
    return () => window.clearInterval(timer);
  }, [reducedMotion]);

  const goToSlide = (slide: number) => setActiveSlide(slide);

  return (
    <section
      className="home-hero-carousel"
      aria-label="The CHI Alliance featured community stories"
    >
      <div className="home-hero-carousel__slides">
        <article className={`home-hero-carousel__slide home-hero-carousel__slide--origin ${activeSlide === 0 ? "is-active" : ""}`} aria-hidden={activeSlide !== 0}>
          <ResponsiveImage src="/media/backgrounds/home-hero-community.webp" alt="" sizes="100vw" className="home-hero-carousel__origin-photo" aria-hidden="true" />
          <div className="home-hero-carousel__origin-scrim" aria-hidden="true" />
          <div className="home-hero-carousel__origin-vignette" aria-hidden="true" />
          <div className="home-hero-carousel__content container">
            <SectionBadge className="home-hero-carousel__origin-eyebrow" variant="gradient">Connecting Human Innovators</SectionBadge>
            <div className="home-hero-headline-lift">
              <h1 className="home-hero-carousel__origin-heading">
                More than a Network.<br />
                <span>More than a Community.</span>
              </h1>
            </div>
            <p className="home-hero-carousel__origin-copy">
              The CHI Alliance is where bold thinkers, innovators, leaders, and lifelong learners connect to grow, collaborate, and thrive. We are a movement dedicated to building authentic relationships, sharing wisdom, and elevating human connections.
            </p>
            <div className="chi-cta-pair justify-center">
              <Link href="/calendar-of-events" className="w-full sm:w-auto" tabIndex={activeSlide === 0 ? 0 : -1}>
                <button className="chi-btn-primary flex items-center justify-center gap-2 w-full sm:w-auto" style={{ padding: "1.05rem 2.7rem", fontSize: "1.05rem" }}>
                  View Upcoming Events <CtaIcon icon={CalendarDays} />
                </button>
              </Link>
              <Link href="/mission-and-values" className="w-full sm:w-auto" tabIndex={activeSlide === 0 ? 0 : -1}>
                <button className="chi-btn-outline w-full sm:w-auto" style={{ padding: "1.05rem 2.7rem", fontSize: "1.05rem", textAlign: "center" }}>
                  Our Mission <CtaIcon icon={Lightbulb} />
                </button>
              </Link>
            </div>
          </div>
        </article>

        <article className={`home-hero-carousel__slide home-hero-carousel__slide--connection ${activeSlide === 1 ? "is-active" : ""}`} aria-hidden={activeSlide !== 1}>
          <ResponsiveImage src={COMMUNITY_HERO_BACKGROUND} alt="" sizes="100vw" className="home-hero-carousel__connection-background" aria-hidden="true" />
          <div className="home-hero-carousel__connection-radial" aria-hidden="true" />
          <div className="home-hero-carousel__connection-container container">
            <div className="home-hero-carousel__connection-copy">
              <p className="home-hero-carousel__connection-eyebrow">Featured Community Spotlight</p>
              <p className="home-hero-carousel__connection-word home-hero-carousel__connection-word--one">One</p>
              <p className="home-hero-carousel__connection-word home-hero-carousel__connection-word--connection">Connection</p>
              <p className="home-hero-carousel__connection-word home-hero-carousel__connection-word--can-change">Can Change</p>
              <p className="home-hero-carousel__connection-word home-hero-carousel__connection-word--everything">Everything!</p>
              <p className="home-hero-carousel__connection-intro">Intentional connections create <span>value</span>, <span>opportunity</span>, and <span>growth</span> for everyone.</p>
              <div className="home-hero-carousel__connection-values" aria-label="Connect, grow, collaborate, and build">
                <span><Users size={28} /><b>Connect</b><small>with intention.</small></span>
                <span><TrendingUp size={28} /><b>Grow</b><small>together.</small></span>
                <span><Handshake size={28} /><b>Collaborate</b><small>create impact.</small></span>
                <span><Heart size={28} /><b>Build</b><small>lasting relationships.</small></span>
              </div>
              <div className="chi-cta-pair home-hero-carousel__connection-actions">
                <Link href="/community" className="w-full sm:w-auto" tabIndex={activeSlide === 1 ? 0 : -1}>
                  <button className="chi-btn-primary flex items-center justify-center gap-2 w-full sm:w-auto" style={{ padding: "0.875rem 2.1rem", fontSize: "0.875rem" }}>
                    Explore Community <CtaIcon icon={Users} />
                  </button>
                </Link>
                <Link href="/calendar-of-events" className="w-full sm:w-auto" tabIndex={activeSlide === 1 ? 0 : -1}>
                  <button className="chi-btn-outline w-full sm:w-auto" style={{ padding: "0.875rem 2.1rem", fontSize: "0.875rem", textAlign: "center" }}>
                    Upcoming Events <CtaIcon icon={CalendarDays} />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div className="home-hero-carousel__controls" role="tablist" aria-label="Hero slides">
        {["The CHI Alliance", "One Connection"].map((label, index) => (
          <button
            key={label}
            type="button"
            role="tab"
            aria-selected={activeSlide === index}
            aria-label={`Show ${label} slide`}
            className={activeSlide === index ? "is-active" : ""}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash !== "#community-story") return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("community-story")?.scrollIntoView({ block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div style={{ background: "#080C18", minHeight: "100vh" }}>
      <Navigation />

      {/*
        ── HERO ── one stage, two layers.

        The partner band used to be a separate block beneath the hero, each with its
        own background, and the seam between them read as two sections. Wrapping
        both lets the band sit over the hero's lower edge on wide screens, so the
        slide photograph and its scrim run on underneath the marquee and the whole
        thing is one hero. Below 840px the band is too tall to overlay without
        covering the headline, so there the two stack as before.
      */}
      <div className="home-hero-stage">
        {/*
          The Lunar New Year campaign hero, a single static panel, stands in for
          HomeHeroCarousel from launch through 20 February 2027. The carousel is
          left intact below: restoring it is swapping this one element back.
          Everything under the hero — partners, Meet Harry, fundamentals,
          membership, join, FAQ, footer — is untouched by the campaign.
        */}
        <HomepagePromotion />
        <PremierPartners />
      </div>

      {/* ── MEET HARRY ── */}
      <section style={{ background: "#0D1220", padding: "6rem 0" }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FlowIn direction="left" duration={800}>
              <SectionBadge>Founder</SectionBadge>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontWeight: 800, fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1.1, color: "white", marginBottom: "1.5rem" }}>
                Meet{" "}
                <span style={{ background: "linear-gradient(135deg, #E91E8C, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  Harry
                </span>
              </h2>
              <p style={{ color: "#94A3B8", fontSize: "var(--chi-body)", lineHeight: 1.8, fontFamily: "'Inter', sans-serif", marginBottom: "1.25rem" }}>
                Harry is a global Human Resources executive with 20+ years of experience partnering with startups, high-growth organizations, and Fortune 500 companies to build people strategies, develop leaders, strengthen cultures, and drive business transformation.
              </p>
              <p style={{ color: "#94A3B8", fontSize: "var(--chi-body)", lineHeight: 1.8, fontFamily: "'Inter', sans-serif", marginBottom: "2rem" }}>
                The CHI Alliance was born from Harry's belief that the most valuable opportunities come through relationships, shared wisdom, and authentic human connection. His vision is to create a trusted community where professionals can connect, learn, collaborate, and grow together.
              </p>
              <Link href="/mission-and-values">
                <button className="chi-btn-outline flex items-center gap-2">
                  Our Mission & Values <CtaIcon icon={Lightbulb} />
                </button>
              </Link>
            </FlowIn>
            <ScaleReveal delay={150}>
              {/* Outer wrapper — overflow visible so photo bleeds above the card */}
              <div style={{ position: "relative", paddingTop: "80px" }}>
                {/* Photo — bleeds above the card edge, hover reveals play button */}
                <div
                  onClick={() => setVideoModalOpen(true)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 2,
                    cursor: "pointer",
                  }}
                  className="group"
                >
                  <div style={{ position: "relative", width: 160, height: 160 }}>
                    <img
                      src="/media/speakers/harry-chiwiz.webp"
                      alt="The CHI Alliance founder video"
                      // Matches the rendered box exactly, so the circle reserves
                      // its space before the file arrives.
                      width={160}
                      height={160}
                      // Several screens below the fold — behind the hero, the
                      // partner band and the stats bar.
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: "160px",
                        height: "160px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        objectPosition: "center top",
                        border: "3px solid rgba(233,30,140,0.6)",
                        boxShadow: "0 0 40px rgba(233,30,140,0.5), 0 8px 32px rgba(0,0,0,0.6)",
                        display: "block",
                        transition: "filter 0.3s ease",
                      }}
                      className="group-hover:brightness-50"
                    />
                    {/* Play overlay */}
                    <div
                      className="group-hover:opacity-100"
                      style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        background: "rgba(0,0,0,0.2)",
                      }}
                    >
                      <div style={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #E91E8C, #F5A623)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 20px rgba(233,30,140,0.6)",
                      }}>
                        <Play size={22} fill="white" color="white" style={{ marginLeft: 3 }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div style={{
                  background: "linear-gradient(160deg, rgba(233,30,140,0.10) 0%, rgba(13,18,32,0.95) 40%, rgba(0,212,255,0.06) 100%)",
                  border: "1px solid rgba(233,30,140,0.25)",
                  borderRadius: "1.5rem",
                  padding: "6rem 2.5rem 2.5rem",
                  position: "relative",
                  overflow: "hidden",
                  textAlign: "center",
                }}>
                  <FloatingOrb color="rgba(233, 30, 140, 0.12)" size={300} duration={10} intensity={50} />

                  {/* Name + title */}
                  <div style={{ position: "relative", zIndex: 1, marginBottom: "1.5rem" }}>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontWeight: 700, fontSize: "1.6rem", color: "white", letterSpacing: "0.04em" }}>Harry</div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", color: "#E91E8C", letterSpacing: "0.12em", textTransform: "uppercase", marginTop: "0.25rem" }}>Founder, The CHI Alliance</div>
                  </div>

                  {/* Divider */}
                  <div style={{ width: "40px", height: "2px", background: "linear-gradient(90deg,#E91E8C,#F5A623)", margin: "0 auto 1.5rem", borderRadius: "2px", position: "relative", zIndex: 1 }} />

                  {/* Quote */}
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontStyle: "italic", fontSize: "clamp(1.35rem, 2.2vw, 1.75rem)", color: "#F1F5F9", lineHeight: 1.55, fontWeight: 300, letterSpacing: "-0.01em" }}>
                      “The most valuable opportunities come through relationships, shared wisdom, and authentic human connection.”
                    </p>
                  </div>
                </div>
              </div>
            </ScaleReveal>
          </div>
        </div>
      </section>

      {/* ── CHI FUNDAMENTALS ── */}
      <HomeFundamentalsExperience />

      {/* ── MEMBERSHIP + COMMUNITY ── */}
      <HomeMembershipCommunityHero />

      {/* ── JOIN CTA ── */}
      <section
        style={{
          position: "relative",
          // Taller than the 7rem the other sections use. The masked mosaic behind
          // this one fades out at both edges, so it needs room above and below the
          // copy for that falloff to happen where the eye is not reading.
          padding: "clamp(8rem, 11vw, 10.5rem) 0",
          overflow: "hidden",
          background: "#080C18",
        }}
      >

        {/* Community mosaic background — fades top/bottom, slightly more visible in center */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/media/backgrounds/community-mosaic.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center center",
            opacity: 0.22,
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 18%, rgba(0,0,0,1) 40%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.7) 82%, transparent 100%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 18%, rgba(0,0,0,1) 40%, rgba(0,0,0,1) 60%, rgba(0,0,0,0.7) 82%, transparent 100%)",
          }}
        />
        {/* Warm magenta/gold radial glow to tie into brand palette */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 50%, rgba(233,30,140,0.08) 0%, rgba(245,166,35,0.04) 40%, transparent 70%)",
          }}
        />
        {/* Dark vignette edges */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(8,12,24,0.7) 100%)",
          }}
        />
        <FadeUp duration={900} distance={32} style={{ position: "relative", zIndex: 10 }}>
        <div className="container text-center">
          <SectionBadge>Join the Movement</SectionBadge>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontWeight: 800, fontSize: "clamp(1.75rem, 3.5vw, 3rem)", color: "white", marginBottom: "1rem" }}>
            Ready to{" "}
            <span style={{ background: "linear-gradient(135deg, #E91E8C, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Connect, Grow & Collaborate?
            </span>
          </h2>
          <p style={{ color: "#94A3B8", fontSize: "var(--chi-body)", lineHeight: 1.75, maxWidth: "560px", margin: "0 auto 2.5rem", fontFamily: "'Inter', sans-serif" }}>
            Subscribe to receive the latest updates on upcoming events, roundtables, networking opportunities, and community activities.
          </p>
          <Link href="/calendar-of-events">
            <button className="chi-btn-primary flex items-center gap-2 mx-auto" style={{ padding: "1rem 2.5rem", fontSize: "0.95rem" }}>
              Explore Events & Register <CtaIcon icon={CalendarDays} size={17} />
            </button>
          </Link>
        </div>
        </FadeUp>
      </section>

      {/* ── FAQ ── */}
      <section style={{ background: "#0D1220", padding: "6rem 0" }}>
        <div className="container">
          <FadeUp duration={700}>
          <div className="text-center mb-14">
            <SectionBadge>Got Questions?</SectionBadge>
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontWeight: 800, fontSize: "clamp(1.75rem, 3.5vw, 3rem)", color: "white" }}>
              Frequently Asked{" "}
              <span style={{ background: "linear-gradient(135deg, #E91E8C, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Questions
              </span>
            </h2>
          </div>
          </FadeUp>
          <EditorialAccordionShowcase
            ariaLabel="Frequently asked questions"
            items={faqs.map(({ q, a }) => ({ title: q, body: a }))}
          />
        </div>
      </section>


      <MembershipBanner
        headingLead="Meet the people"
        headingAccent="behind the work."
        copy={"Full member bios, verified profile links, priority event registration, and the member badge — membership is free."}
      />

      <Footer />

      <style>{`
        /* Two stacked layers, both filling the card, so a crossfade has nothing to
           reflow. Positioned rather than in flow for the same reason. */
        /* object-fit alone, with no object-position: cover scales each photo to fill
           the card and centres it, which is the same framing for all five rather
           than a crop tuned per image. */
        /*
          Keep the upper edge of a source photo visible. These are often group
          portraits, and a centred cover crop can remove foreheads before the
          browser has any chance to help. The card still fills edge-to-edge, but
          the crop gives priority to the people rather than the floor.
        */
        .home-community-card__photo { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:center top; opacity:0; will-change:opacity,transform; }
        .home-community-card__scrim { position:absolute; inset:0; z-index:1; background:linear-gradient(180deg,rgba(8,12,24,.03) 10%,rgba(8,12,24,.26) 40%,rgba(8,12,24,.93) 100%); pointer-events:none; }
        .home-community-card__statement { position:absolute; z-index:2; left:clamp(1.25rem,4vw,3.5rem); right:clamp(1.25rem,4vw,3.5rem); bottom:clamp(1.25rem,4vw,3.5rem); }
        .home-hero-headline-lift { position:relative; z-index:1; display:inline-grid; place-items:center; max-width:min(100%,62rem); margin:0 auto 1.85rem; padding:clamp(.45rem,1.7vw,1.1rem) clamp(.25rem,1.5vw,1rem); isolation:isolate; }
        .home-hero-headline-lift::before { position:absolute; z-index:-1; top:50%; left:50%; width:clamp(29rem,78vw,66rem); aspect-ratio:1; content:""; border-radius:50%; transform:translate(-50%,-50%); background:radial-gradient(circle,rgba(5,10,22,.78) 0%,rgba(6,11,24,.62) 26%,rgba(6,11,24,.36) 47%,rgba(6,11,24,.12) 62%,transparent 78%); filter:blur(15px); opacity:.92; pointer-events:none; }
        /* The statement now sits on the photograph itself. The panel it used to
           live in — border, gradient fill, blur, inset highlight, and the gold blob
           behind its corner — is gone, so legibility comes from the scrim above plus
           the layered shadow on the type below. */
        /*
          A different voice from the section headline above it. That headline is
          Bebas Neue — condensed, uppercase, the display face. These taglines were a
          serif display face, which read as a second headline competing with it, so
          they move to the sans the body copy uses, at a lighter weight and open
          tracking. Display type states; this speaks.

          Two shadows, not one: a tight dark pass to hold the letterforms against a
          busy photo, and a wide soft pass to separate the block from it.
        */
        .home-rotating-statement__message p { display:flex; align-items:flex-end; width:100%; height:100%; color:#FFFFFF; font-family:'DM Sans','Inter',sans-serif; font-size:clamp(1.15rem,1.95vw,2rem); font-weight:350; letter-spacing:-.01em; line-height:1.32; text-shadow:0 2px 5px rgba(0,0,0,.9),0 10px 34px rgba(0,0,0,.72); }
         /* The fixed nav overlays the hero, so the hero can share the first
            viewport with the partner wall instead of pushing it below the fold. */
         .home-hero-carousel { position:relative; height:calc(100svh - var(--chi-partners-band)); min-height:0; overflow:hidden; background:#080C18; isolation:isolate; border-bottom:1px solid rgba(255,255,255,.08); }
        /* The stage composes hero and band. On wide screens the hero takes the whole
           viewport and the band is laid over its bottom edge: absolute, the height of
           the shared token, translucent and blurred so the photograph shows through.
           Each slide pads its bottom by the same token so headline and buttons centre
           in the space above the band rather than behind it, and the slide dots lift
           by the same amount. The photograph and scrim are positioned against the
           slide's full box, so they still run under the band. */
        .home-hero-stage { position:relative; }
        @media (min-width:841px) {
          .home-hero-stage .home-hero-carousel { height:100svh; }
          .home-hero-stage .home-hero-carousel__slide { box-sizing:border-box; padding-bottom:var(--chi-partners-band); }
          .home-hero-stage .home-hero-carousel__controls { bottom:calc(var(--chi-partners-band) + clamp(.9rem,2.2vw,1.7rem)); }
          .home-hero-stage .premier-partners { position:absolute; left:0; right:0; bottom:0; z-index:5; background:rgba(8,12,24,.58); -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px); border-top:1px solid rgba(255,255,255,.09); border-bottom:0; }
        }
        /* Only the LNY campaign reserves space for an overlaid sponsor band.
           Other promotion layouts keep the band in flow so it cannot cover CTAs. */
        .home-hero-stage:not(:has(.lny-hero)) .premier-partners { position:relative; left:auto; right:auto; bottom:auto; }
        .home-hero-carousel__slides { position:relative; height:100%; min-height:100%; }
        .home-hero-carousel__slide { position:absolute; inset:0; display:grid; height:100%; min-height:100%; opacity:0; pointer-events:none; transition:opacity 760ms cubic-bezier(.23,1,.32,1); overflow:hidden; }
        .home-hero-carousel__slide.is-active { position:absolute; opacity:1; pointer-events:auto; z-index:1; }
        .home-hero-carousel__origin { min-height:100%; box-sizing:border-box; place-items:center; text-align:center; padding:clamp(7.5rem,11.8vw,9.25rem) 0 clamp(4rem,6vw,5.75rem); }
         /* Keep the full-height source frame visible so the top row of faces never
            loses its hairline to the hero's cover crop. */
         .home-hero-carousel__origin-photo { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:center top; opacity:.48; }
        .home-hero-carousel__origin-scrim { position:absolute; inset:0; background:linear-gradient(180deg,rgba(8,12,24,.78) 0%,rgba(8,12,24,.18) 35%,rgba(8,12,24,.16) 56%,rgba(8,12,24,.86) 100%),radial-gradient(ellipse at 15% 60%,rgba(233,30,140,.2),transparent 50%),radial-gradient(ellipse at 85% 25%,rgba(0,212,255,.12),transparent 45%); }
        .home-hero-carousel__origin-vignette { position:absolute; inset:0; background:linear-gradient(90deg,rgba(8,12,24,.72),transparent 25%,transparent 75%,rgba(8,12,24,.72)); }
        .home-hero-carousel__content { position:relative; z-index:3; display:flex; flex-direction:column; align-items:center; justify-content:center; max-width:72rem; }
        .home-hero-carousel__origin-eyebrow { position:relative; z-index:4; isolation:isolate; transform:translateY(clamp(1.4rem,2.2vw,2rem)); }
        .home-hero-carousel__origin-heading { margin:0; color:#fff; font-family:'DM Serif Display',serif; font-size:clamp(2.75rem,5.4vw,5.55rem); font-weight:400; line-height:.98; letter-spacing:-.04em; text-align:center; text-shadow:0 8px 34px rgba(0,0,0,.72); }
        .home-hero-carousel__origin-heading span { display:inline-block; margin-top:.12em; background:linear-gradient(100deg,#F5A623,#FB7185 50%,#E91E8C); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
        .home-hero-carousel__origin-copy { max-width:43rem; margin:0 auto 2rem; color:#E2E8F0; font-family:'Inter',sans-serif; font-size:var(--chi-body); line-height:1.7; text-align:center; text-shadow:0 2px 12px rgba(0,0,0,.8); }
        .home-hero-carousel__connection { min-height:100%; background:#080C18; }
        .home-hero-carousel__connection-background { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:center center; transform:scale(1.15); }
        .home-hero-carousel__connection-radial { position:absolute; z-index:1; inset:0; pointer-events:none; background:radial-gradient(ellipse 54% 70% at 24% 34%,rgba(233,30,140,.34) 0%,rgba(162,59,158,.19) 34%,rgba(8,12,24,.16) 59%,transparent 76%); }
         .home-hero-carousel__connection-container { position:relative; z-index:2; min-height:100%; box-sizing:border-box; display:grid; grid-template-columns:1fr; align-items:center; justify-items:center; padding:clamp(5rem,8vw,7.5rem) 0 clamp(4rem,6vw,5.75rem); }
        .home-hero-carousel__connection-copy { justify-self:center; width:min(100%,30rem); text-align:center; transform:scale(1.2); transform-origin:center; }
        .home-hero-carousel__connection-eyebrow { display:table; width:max-content; max-width:100%; margin:0 auto clamp(.7rem,1.2vw,1rem); padding:.42rem .82rem; border:1px solid rgba(255,255,255,.55); border-radius:999px; color:#fff; background:linear-gradient(100deg,rgba(245,166,35,.88),rgba(233,30,140,.94) 55%,rgba(139,92,246,.9)); box-shadow:0 8px 22px rgba(8,12,24,.38),0 0 24px rgba(233,30,140,.22); font-family:'Space Grotesk',sans-serif; font-size:clamp(.66rem,.8vw,.76rem); font-weight:800; letter-spacing:.075em; line-height:1; text-transform:uppercase; text-shadow:0 1px 3px rgba(8,12,24,.62); }
        .home-hero-carousel__connection-word { margin:0; color:#fff; text-transform:uppercase; font-family:'Bebas Neue',sans-serif; font-weight:800; letter-spacing:.015em; line-height:.78; filter:drop-shadow(0 8px 18px rgba(0,0,0,.62)); }
        .home-hero-carousel__connection-word--one { font-size:clamp(2.65rem,4.65vw,4.6rem); }
        .home-hero-carousel__connection-word--connection { margin:.04em 0 -.04em; color:#F5A623; font-family:'DM Serif Display',serif; font-size:clamp(3rem,6.1vw,5.8rem); font-style:italic; letter-spacing:-.08em; text-transform:none; transform:rotate(-4deg); transform-origin:center; }
        .home-hero-carousel__connection-word--can-change { font-size:clamp(2.45rem,4.4vw,4.25rem); }
        .home-hero-carousel__connection-word--everything { margin-top:.05em; font-size:clamp(2.9rem,5.45vw,5.25rem); background:linear-gradient(90deg,#41C653,#F5A623 46%,#E91E8C 78%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
        .home-hero-carousel__connection-intro { max-width:34rem; margin:clamp(1.2rem,2vw,1.75rem) auto; color:#F1F5F9; font-family:'Inter',sans-serif; font-size:clamp(.98rem,1.25vw,1.18rem); line-height:1.55; text-shadow:0 2px 13px rgba(0,0,0,.9); }
        .home-hero-carousel__connection-intro span:nth-child(1) { color:#65D66A; } .home-hero-carousel__connection-intro span:nth-child(2) { color:#F472B6; } .home-hero-carousel__connection-intro span:nth-child(3) { color:#FCD34D; }
        .home-hero-carousel__connection-values { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:.15rem; margin:0 auto clamp(1.45rem,2.5vw,2rem); }
        .home-hero-carousel__connection-values span { min-width:0; display:grid; justify-items:center; gap:.25rem; padding:0 .35rem; border-left:1px solid rgba(245,166,35,.55); color:#F472B6; }
        .home-hero-carousel__connection-values span:first-child { border-left:0; } .home-hero-carousel__connection-values span:nth-child(2) { color:#65D66A; } .home-hero-carousel__connection-values span:nth-child(3) { color:#FCD34D; } .home-hero-carousel__connection-values span:nth-child(4) { color:#F472B6; }
        .home-hero-carousel__connection-values b { color:currentColor; font-family:'Bebas Neue',sans-serif; font-size:clamp(1.12rem,1.55vw,1.42rem); letter-spacing:.035em; line-height:1; text-transform:uppercase; text-shadow:0 2px 11px rgba(0,0,0,.8); } .home-hero-carousel__connection-values small { color:#F8FAFC; font-family:'Inter',sans-serif; font-size:clamp(.61rem,.78vw,.75rem); line-height:1.18; text-shadow:0 1px 8px rgba(0,0,0,.85); }
        .home-hero-carousel__connection-actions { justify-content:center; }
        .home-hero-carousel__controls { position:absolute; z-index:4; left:50%; bottom:clamp(.9rem,2.2vw,1.7rem); display:flex; gap:.55rem; transform:translateX(-50%); }
        .home-hero-carousel__controls button { width:2.7rem; height:.32rem; border:0; border-radius:999px; background:rgba(255,255,255,.35); box-shadow:0 2px 7px rgba(0,0,0,.45); transition:width 180ms ease,background 180ms ease; } .home-hero-carousel__controls button.is-active { width:4.3rem; background:linear-gradient(90deg,#F5A623,#E91E8C); }
          @media (min-width:701px) and (max-width:1050px) { .home-hero-carousel__connection-background { background-position:62% center; transform:scale(1.07); } .home-hero-carousel__connection-radial { background:radial-gradient(ellipse 72% 76% at 50% 38%,rgba(233,30,140,.38) 0%,rgba(162,59,158,.22) 35%,rgba(8,12,24,.2) 63%,transparent 78%); } .home-hero-carousel__connection-container { grid-template-columns:1fr; padding:clamp(5.25rem,8vw,6.5rem) 0 clamp(4rem,6vw,5rem); } .home-hero-carousel__connection-copy { justify-self:center; width:min(100%,31.5rem); margin-left:0; transform:scale(1.06); transform-origin:center; } .home-hero-carousel__connection-values { max-width:30rem; gap:clamp(.5rem,1vw,.85rem); } .home-hero-carousel__connection-values span { padding:0 clamp(.18rem,.45vw,.45rem); } .home-hero-carousel__connection-values b { font-size:clamp(1.08rem,1.8vw,1.28rem); } .home-hero-carousel__connection-values small { font-size:clamp(.59rem,.72vw,.68rem); } }
         @media (min-width:841px) and (max-height:800px) { .home-hero-carousel__origin { padding:6.5rem 0 2rem; } .home-hero-carousel__origin-heading { font-size:clamp(2.6rem,4.8vw,4.6rem); } .home-hero-carousel__origin-copy { margin-bottom:1.25rem; line-height:1.5; } .home-hero-carousel__connection-container { padding:4.7rem 0 2.5rem; } .home-hero-carousel__connection-copy { transform:scale(.88); } .home-hero-carousel__connection-intro { margin:.85rem auto; } .home-hero-carousel__connection-values { margin-bottom:1.1rem; } }
         @media (max-width:700px) { .home-hero-carousel { height:100svh; min-height:100svh; } .home-hero-carousel__origin { padding:clamp(6.5rem,20vw,8.5rem) 0 clamp(4.5rem,10vw,5.75rem); } .home-hero-carousel__origin-heading { font-size:clamp(2.55rem,11.6vw,4rem); } .home-hero-carousel__origin-copy { font-size:.95rem; } .home-hero-carousel__connection-background { background-position:58% center; transform:scale(1.1); } .home-hero-carousel__connection-container { grid-template-columns:1fr; align-items:center; padding:clamp(6rem,20vw,8rem) 0 clamp(4.25rem,9vw,5.5rem); } .home-hero-carousel__connection-copy { width:min(90%,31rem); transform:scale(1.1); } }
        @media (max-width:560px) { .home-community-card { height:31rem; } .home-community-card__statement { left:1rem; right:1rem; bottom:1rem; } .home-hero-eyebrow { margin-bottom:1.15rem !important; } .home-hero-headline-lift { width:100%; margin-bottom:1.4rem; padding:.25rem .15rem; } .home-hero-headline-lift::before { width:155vw; } .home-rotating-statement__message { height:8rem; } .home-rotating-statement__message p { font-size:clamp(1.02rem,4.4vw,1.35rem); line-height:1.4; } .home-hero-carousel__connection-background { background-position:62% center; transform:scale(1.06); } .home-hero-carousel__connection-copy { transform:scale(1.04); } .home-hero-carousel__connection-values { max-width:23rem; } .home-hero-carousel__connection-values svg { width:24px; height:24px; } .home-hero-carousel__connection-values b { font-size:clamp(1.03rem,4.1vw,1.2rem); } .home-hero-carousel__connection-values small { font-size:.59rem; } }
        @media (min-width:701px) and (max-width:1050px) { .home-hero-carousel__connection-background { object-position:62% center; } }
        @media (max-width:700px) { .home-hero-carousel__connection-background { object-position:58% center; } }
        @media (max-width:560px) { .home-hero-carousel__connection-background { object-position:62% center; } }
        @media (prefers-reduced-motion:reduce) { .home-rotating-statement__message p:not(:first-child) { display:none; } .home-hero-carousel__slide { transition:none; } }
        @keyframes meshPulse {
          0% { opacity: 0.8; }
          50% { opacity: 1; }
          100% { opacity: 0.8; }
        }
        @keyframes chi-pulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
